import { useRef, useState } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { Button, Modal } from '@shared/ui';
import { productsApi, type ProductImportResult } from '../../api/products.api';
import './ImportProductsModal.css';

type ImportProductsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

async function readApiError(error: unknown, fallback: string): Promise<string> {
  if (!axios.isAxiosError(error)) return fallback;
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as { message?: unknown };
      return messageText(parsed.message) ?? fallback;
    } catch {
      return fallback;
    }
  }
  if (data && typeof data === 'object' && 'message' in data) {
    return messageText((data as { message?: unknown }).message) ?? fallback;
  }
  return fallback;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function messageText(message: unknown): string | null {
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message)) {
    const text = message.map(String).filter(Boolean).join('; ');
    return text || null;
  }
  return null;
}

export default function ImportProductsModal({ isOpen, onClose }: ImportProductsModalProps) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [result, setResult] = useState<ProductImportResult | null>(null);
  const [requestError, setRequestError] = useState('');

  const importMutation = useMutation({
    mutationFn: (nextFile: File) => productsApi.importFromExcel(nextFile),
    onSuccess: (data) => {
      setResult(data);
      setRequestError('');
      if (data.created.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
        queryClient.invalidateQueries({ queryKey: ['products', 'seller-categories'] });
        queryClient.invalidateQueries({ queryKey: ['products', 'collections'] });
        notifications.show({
          title: 'Импорт завершён',
          message: `Создано товаров: ${data.created.length}`,
          color: 'green',
        });
      }
    },
  });

  const reset = () => {
    setFile(null);
    setResult(null);
    setRequestError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    if (importMutation.isPending) return;
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    setRequestError('');
    try {
      await productsApi.downloadImportTemplate();
    } catch (error: unknown) {
      setRequestError(await readApiError(error, 'Не удалось скачать шаблон'));
    } finally {
      setTemplateLoading(false);
    }
  };

  const acceptFile = (next: File | null) => {
    if (!next || importMutation.isPending) return;
    const name = next.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.csv')) {
      setFile(null);
      setRequestError('Загрузите файл .xlsx');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(next);
    setResult(null);
    setRequestError('');
  };

  const handleImport = async () => {
    if (!file) return;
    setResult(null);
    setRequestError('');
    try {
      await importMutation.mutateAsync(file);
    } catch (error: unknown) {
      setRequestError(await readApiError(error, 'Не удалось импортировать файл'));
    }
  };

  const hiddenCount = result?.created.filter((item) => item.hidden).length ?? 0;
  const updatedCount = result?.created.filter((item) => item.updated).length ?? 0;
  const createdCount = (result?.created.length ?? 0) - updatedCount;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Импорт товаров" maxWidth={560}>
      <div className="import-products">
        <p className="import-products__lead">
          Заполните шаблон и загрузите файл .xlsx. Одна строка — один товар. Если название уже есть, товар обновится. Новые и изменённые товары уходят на модерацию.
        </p>
        <ul className="import-products__hints">
          <li>Обязательно: название, цена, категория, состав, высота, ширина и вес.</li>
          <li>Для букета ещё нужны тип цветка, тип сборки и цвет. Если в названии категории написано «не цветы», тип цветка и тип сборки не нужны, цвет можно указать.</li>
          <li>Категорию и тип сборки выбирайте из списка в ячейке. Типы цветков и цвета пишите текстом: одно название или несколько через точку с запятой, например «роза; эвкалипт» и «красный; белый». Если такого типа или цвета нет, строка не загрузится.</li>
          <li>Состав пишите в одной ячейке: название и количество через двоеточие, несколько позиций через точку с запятой. Пример: Роза:11; Эвкалипт:3.</li>
          <li>В шаблоне уже есть 5 примеров. Замените их своими товарами или удалите перед загрузкой.</li>
          <li>Остаток записывается на все точки самовывоза. Пусто — 0, «безлимит» — без ограничения.</li>
          <li>Фото — ссылки, по одной в ячейке. Если нужно больше трёх, скопируйте колонку «Фото» рядом: копию можно назвать «Фото 4» или тоже «Фото». Всего 10 фото. Если их меньше трёх, товар создаётся скрытым.</li>
        </ul>

        <button
          type="button"
          className="import-products__template"
          onClick={handleDownloadTemplate}
          disabled={templateLoading || importMutation.isPending}
        >
          {templateLoading ? 'Скачиваем шаблон…' : 'Скачать шаблон'}
        </button>

        <label
          className={`import-products__drop${file ? ' import-products__drop--filled' : ''}${dragOver ? ' import-products__drop--over' : ''}${importMutation.isPending ? ' import-products__drop--busy' : ''}`}
          onDragEnter={(event) => {
            event.preventDefault();
            if (!importMutation.isPending) setDragOver(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!importMutation.isPending) setDragOver(true);
          }}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setDragOver(false);
            }
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            acceptFile(event.dataTransfer.files?.[0] ?? null);
          }}
        >
          <input
            ref={inputRef}
            className="import-products__drop-input"
            type="file"
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            disabled={importMutation.isPending}
            onChange={(event) => acceptFile(event.target.files?.[0] ?? null)}
          />
          {file ? (
            <span className="import-products__drop-file">
              <span className="import-products__drop-name">{file.name}</span>
              <span className="import-products__drop-meta">{formatFileSize(file.size)} · нажмите, чтобы заменить</span>
            </span>
          ) : (
            <>
              <span className="import-products__drop-title">Выберите файл или перетащите сюда</span>
              <span className="import-products__drop-meta">Excel, .xlsx</span>
            </>
          )}
        </label>
        {file ? (
          <button
            type="button"
            className="import-products__clear"
            onClick={reset}
            disabled={importMutation.isPending}
          >
            Убрать файл
          </button>
        ) : null}

        {requestError ? <p className="import-products__error">{requestError}</p> : null}

        {result ? (
          <div className="import-products__result">
            <p>
              Создано: {createdCount}. Обновлено: {updatedCount}
              {result.failed.length > 0 ? `. С ошибками: ${result.failed.length}` : ''}
              {hiddenCount > 0 ? `. Скрыто без фото: ${hiddenCount}` : ''}
            </p>
            {result.failed.length > 0 ? (
              <ul className="import-products__errors">
                {result.failed.map((item) => (
                  <li key={`${item.row}-${item.name}`}>
                    Строка {item.row}
                    {item.name ? ` (${item.name})` : ''}: {item.errors.join('; ')}
                  </li>
                ))}
              </ul>
            ) : null}
            {result.created.some((item) => item.warnings.some((warning) => !warning.startsWith('Меньше 3 фото'))) ? (
              <ul className="import-products__errors">
                {result.created.flatMap((item) =>
                  item.warnings
                    .filter((warning) => !warning.startsWith('Меньше 3 фото'))
                    .map((warning) => (
                      <li key={`${item.id}-${warning}`}>
                        Строка {item.row} ({item.name}): {warning}
                      </li>
                    )),
                )}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="import-products__actions">
          <Button variant="secondary" onClick={handleClose} disabled={importMutation.isPending}>
            Закрыть
          </Button>
          <Button onClick={handleImport} loading={importMutation.isPending} disabled={!file}>
            Загрузить
          </Button>
        </div>
      </div>
    </Modal>
  );
}
